import { EMessengerChatSourceType } from '@xometry/ui';
import { InvoiceState } from 'interfaces';
import { RootStore } from 'stores/RootStore';

interface GetDocumentTitleArgs {
  store: RootStore;
  sourceType: EMessengerChatSourceType;
  /** Route-derived fallback used when the per-deal store has no matching label for the requested object. */
  objectTitle?: string;
  /**
   * The requested object's id. Store-derived labels are trusted only when the loaded object's id matches it,
   * so the chat header never shows a stale (previous-object) or numberless title (EUO-231 Bug 2 retest).
   */
  sourceId?: string | number;
}

interface LabelForLoadedIdArgs {
  /** ID of the object currently held by the per-deal store, compared against the requested `sourceId`. */
  loadedId: string | number | null | undefined;
  /** Builds the store-derived label; invoked only when `loadedId` matches the requested `sourceId`. */
  label: () => string;
  /** Last-resort label when neither the matched store label nor `objectTitle` yields a value. */
  fallback?: string;
}

export const getDocumentTitle = ({ store, sourceType, objectTitle, sourceId }: GetDocumentTitleArgs) => {
  /**
   * The per-deal MobX stores (jobStore, dealProviderOrderStore, dealOutInvoiceEditStore, …) hold the *last
   * visited* object and lag one navigation behind the URL when the user switches objects with the compact
   * messenger open. Use the store-derived `label()` only when the loaded object's id matches the requested
   * `sourceId`; otherwise prefer the route-derived `objectTitle` (e.g. `J-{pd}-{id}`), then `fallback`.
   * Without this, the chat header showed the PREVIOUS object's number — or `J--` before the store loaded completely * (EUO-231 Bug 2 retest).
   */
  const labelForLoadedId = ({ loadedId, label, fallback = '' }: LabelForLoadedIdArgs): string => {
    const matches = loadedId != null && sourceId != null && String(loadedId) === String(sourceId);

    return (matches ? label() : '') || objectTitle || fallback;
  };

  const sourceTypeTitleMap: Record<EMessengerChatSourceType, (() => string) | undefined> = {
    [EMessengerChatSourceType.DEAL]: () => `D-${store.dealStore.data.id}`,
    [EMessengerChatSourceType.ORDER_CONFIRMATION]: () =>
      labelForLoadedId({
        loadedId: store.orderConfirmationStore.orderConfirmation?.id,
        label: () => store.orderConfirmationStore.orderConfirmation.number,
      }),
    [EMessengerChatSourceType.ENQUIRY]: () =>
      labelForLoadedId({
        loadedId: store.enquiriesStore.enquiry?.id,
        label: () => store.enquiriesStore.enquiry.number,
      }),
    [EMessengerChatSourceType.STORAGE]: () =>
      labelForLoadedId({
        loadedId: store.storageStore.currentStorage?.id,
        label: () => store.storageStore.currentStorage.number,
      }),
    [EMessengerChatSourceType.PROVIDER_ORDER]: () =>
      labelForLoadedId({
        loadedId: store.dealProviderOrderStore.currentProviderOrder?.id,
        label: () => store.dealProviderOrderStore.currentProviderOrder?.number || '',
        fallback: 'PO',
      }),
    [EMessengerChatSourceType.OUTCOMING_INVOICE]: () => {
      const data = store.dealOutInvoiceEditStore.data;

      /**
       * Use the edit-store label only when it holds *this* invoice; otherwise fall back to the route-derived
       * label (`OI-{pd}-{id}`). Covers both the not-yet-loaded case (sidebar click without visiting the page)
       * and the stale case (navigated to another invoice while the messenger stayed open) — EUO-231 Bug 2.
       */
      if (!data || (sourceId != null && String(data.id) !== String(sourceId))) {
        return objectTitle || 'Invoice';
      }

      const status = data.state === InvoiceState.Published ? '' : ` ${data.state}`;
      const invoiceTypeTxt = store.dealOutInvoiceEditStore.isCreditNote ? 'Credit Note' : 'Invoice';

      return `${invoiceTypeTxt}${status} #${data.id}`;
    },
    [EMessengerChatSourceType.INCOMING_INVOICE]: () => {
      const invoice = store.incomingInvoicesStore.invoice;
      const isIncCreditNote = invoice?.creditNote || false;
      const invoiceName = store.incomingInvoicesStore.invoiceName;

      const invoiceType = (isCreditNote: boolean) => (isCreditNote ? 'Credit Note' : 'Invoice');
      const xometryCNType = (isCreditNote: boolean) => (isCreditNote ? 'Incoming' : 'Partner');

      /**
       * Same guard as OUTCOMING_INVOICE: prefer the route label (`II-{pd}-{id}`) when the store has not loaded
       * this invoice or still holds a previously opened one (EUO-231 Bug 2).
       */
      if (!invoice?.id || (sourceId != null && String(invoice.id) !== String(sourceId))) {
        return objectTitle || `New Incoming ${invoiceType(isIncCreditNote)}`;
      }

      if (store.incomingInvoicesStore.isXometryCreditNote) {
        return `${xometryCNType(isIncCreditNote)} Credit Note ${invoiceName} (${invoice.state || ''})`;
      } else {
        return `Incoming ${invoiceType(isIncCreditNote)} ${invoiceName} (${invoice.state || ''})`;
      }
    },
    /**
     * `objectTitle` fallback (EUO-231 Bug 2): callers from `buildDealMessengerChatSource` pass an ID-qualified
     * label here (`PI-{pd}-{id}`, `QC-{pd}-{id}`, `CL-{pd}-{id}`, `PZ-{pd}-{id}`). Use the live store number only
     * when it belongs to the requested payment; otherwise prefer the route label so we never show a stale or
     * numberless title.
     */
    [EMessengerChatSourceType.PAYMENT]: () =>
      labelForLoadedId({
        loadedId: store.paymentStore.currentPayment?.id,
        label: () => store.paymentStore.currentPayment?.number || '',
        fallback: 'Payment',
      }),
    [EMessengerChatSourceType.PAYOUT]: () => objectTitle || 'Payout',
    [EMessengerChatSourceType.JOB]: () =>
      labelForLoadedId({
        loadedId: store.jobStore.currentJob?.id,
        label: () => `J-${store.jobStore.currentJob?.dealPipedriveId || ''}-${store.jobStore.currentJob?.id || ''}`,
      }),
    [EMessengerChatSourceType.JOB_OFFER]: () =>
      labelForLoadedId({
        loadedId: store.jobOfferStore.currentJobOffer?.id,
        label: () =>
          `J-${store.jobStore.currentJob?.dealPipedriveId || ''}-${
            store.jobStore.currentJob?.id || ''
          } / Offer #${String(store.jobOfferStore.currentJobOffer?.id)}`,
      }),
    [EMessengerChatSourceType.QUALITY_CONTROL]: () => objectTitle || 'Quality control',
    [EMessengerChatSourceType.CLAIM]: () => objectTitle || 'Claim',
    [EMessengerChatSourceType.SHIPPING]: () =>
      labelForLoadedId({
        loadedId: store.dealShippingStore.shipping?.id,
        label: () => store.dealShippingStore.shipping.number,
      }),
    [EMessengerChatSourceType.MASTER_SHIPPING]: () => objectTitle || 'Master Shipping',
    [EMessengerChatSourceType.BILLING_ACCOUNT]: () => objectTitle || 'Billing Account',
    [EMessengerChatSourceType.ENQUIRY_PART]: undefined,
    [EMessengerChatSourceType.PART]: undefined,
    [EMessengerChatSourceType.CUSTOMER]: () => objectTitle || 'General chat with a customer',
    [EMessengerChatSourceType.PARTNER]: () => objectTitle || 'General chat with a partner',
    [EMessengerChatSourceType.ENQUIRY_RFQ]: undefined,
    [EMessengerChatSourceType.GSH_JOB_OFFER]: undefined,
    [EMessengerChatSourceType.RFQ]: () => objectTitle || 'RFQ',
    [EMessengerChatSourceType.RFQ_OFFER]: () => objectTitle || 'RFQ Offer',
  };

  return sourceTypeTitleMap[sourceType]?.() || '';
};
